import type { ComponentType } from 'react';
import { BanknotesIcon, TruckIcon } from '@heroicons/react/24/outline';
import type { PurchaseOrderDetail } from '../../../services/purchaseOrders';
import { ActionCard } from '../../patient-files/actions/ActionCard';
import { DrawerSection } from '../Drawer';
import { UpdateBillingModal } from './UpdateBillingModal';
import { UpdateShippingModal } from './UpdateShippingModal';

export type PurchaseOrderActionId = 'shipping' | 'billing';

interface PurchaseOrderActionModalProps {
    purchaseOrder: PurchaseOrderDetail;
    onClose: () => void;
}

interface PurchaseOrderActionConfig {
    id: PurchaseOrderActionId;
    label: string;
    icon: ComponentType<{ className?: string }>;
    modal: ComponentType<PurchaseOrderActionModalProps>;
}

const PURCHASE_ORDER_ACTIONS: PurchaseOrderActionConfig[] = [
    {
        id: 'shipping',
        label: 'Update Shipping',
        icon: TruckIcon,
        modal: UpdateShippingModal
    },
    {
        id: 'billing',
        label: 'Update Billing',
        icon: BanknotesIcon,
        modal: UpdateBillingModal
    }
];

interface PurchaseOrderActionsSectionProps {
    onSelect: (id: PurchaseOrderActionId) => void;
}

export function PurchaseOrderActionsSection({ onSelect }: PurchaseOrderActionsSectionProps) {
    return (
        <DrawerSection title="Actions">
            <div className="flex flex-wrap gap-3">
                {PURCHASE_ORDER_ACTIONS.map((action) => (
                    <ActionCard
                        key={action.id}
                        icon={action.icon}
                        label={action.label}
                        onSelect={() => onSelect(action.id)}
                    />
                ))}
            </div>
        </DrawerSection>
    );
}

interface PurchaseOrderActionModalHostProps extends PurchaseOrderActionModalProps {
    actionId: PurchaseOrderActionId;
}

// Rendered outside the drawer so the modal keeps its own stacking context.
export function PurchaseOrderActionModalHost({
    actionId,
    purchaseOrder,
    onClose
}: PurchaseOrderActionModalHostProps) {
    const action = PURCHASE_ORDER_ACTIONS.find((candidate) => candidate.id === actionId);
    if (!action) return null;
    const Modal = action.modal;
    return <Modal purchaseOrder={purchaseOrder} onClose={onClose} />;
}
